import React, { useContext, useEffect, useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Title,
} from "chart.js";
import GraphComponent from "../Components/GraphComponent";
import {
  getDashboardAcquisitionTilesCount,
  getGraphReportLeadSource,
  getGraphReportLeadPaymentInsight,
  getGraphReportLeadConversionPerDay,
  getRedisGraphReportLeadConversionPerDay,
  getRedisDashboardAcquisitionTilesCount,
  getRedisGraphReportLeadSource,
  getRedisGraphReportLeadPaymentInsight,
} from "../Services/dashboardService/dashboardService";
import MUIPairDatePicker from "../Components/MUIPairDatePicker";
import { ToastContext } from "../utils/ToastContext";
import { RefreshButton } from "../Components/RefreshButton";
import { DashboardCard } from "../Components/DashboardCard";
import DashboardTimelineSelector from "../Components/DashboardTimelineSelector";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(isoWeek);
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  Filler,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title
);

const Dashboard = () => {
  useEffect(() => {
    document.title = "Leads Dashboard";
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useContext(ToastContext);
  const [graph1Type, setGraph1Type] = useState("pie");
  const [graph2Type, setGraph2Type] = useState("line");
  const [graph3Type, setGraph3Type] = useState("pie");
  const [fullScreenGraph, setFullScreenGraph] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({
    fromDate: "",
    toDate: "",
  });

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRangeType, setDateRangeType] = useState("today");

  const [metrics, setMetrics] = useState({
    engagement_count: "loading...",
    whatsapp_count: "loading...",
    diy_lead_count: "loading...",
    dropoff_count: "loading...",
    new_leads_count: "loading...",
    outbound_calls: "loading...",
    inbound_calls: "loading...",
    total_calls: "loading...",
    answered_calls: "loading...",
    unanswered_calls: "loading...",
    missed_calls: "loading...",
  });

  const [graphData1, setGraphData1] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [
          "rgba(255, 105, 180,0.7)",
          "rgba(3, 168, 158,0.7)",
          "rgba(54, 162, 235,0.7)",
          "rgba(255, 206, 86,0.7)",
          "rgba(131, 5, 242,0.7)",
          "rgba(100, 5, 242,0.7)",
        ],
      },
    ],
  });

  const [graphData3, setGraphData3] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [
          "rgba(3, 168, 158,0.7)",
          "rgba(255, 206, 86,0.7)",
          "rgba(51, 51, 255,0.7)",
          "rgba(46, 184, 46,0.7)",
          "rgba(255, 105, 180,0.7)",
          "rgba(100, 5, 242,0.7)",
        ],
      },
    ],
  });

  const [multiAxisLineChartData, setMultiAxisLineChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Total Leads",
        data: [],
        fill: false,
        borderColor: "rgb(54, 162, 235)",
        backgroundColor: "rgb(54, 162, 235,0.7)",
      },
      {
        label: "Payment Stage Completed",
        data: [],
        fill: false,
        borderColor: "rgb(75, 192, 192)",
        backgroundColor: "rgba(75, 192, 192,0.7)",
      },
    ],
  });

  useEffect(() => {
    getDataByRedis("today");
  }, []);

  const handleRefresh = () => {
    if (dateRangeType === "custom") {
      leadMetrics();
      leadSources();
      leadPaymentInsight();
      leadConversionGraphPerDay();
    } else {
      getDataByRedis(dateRangeType);
    }
  };

  const leadMetrics = async () => {
    setMetrics({
      engagement_count: "loading...",
      whatsapp_count: "loading...",
      diy_lead_count: "loading...",
      dropoff_count: "loading...",
      new_leads_count: "loading...",
      outbound_calls: "loading...",
      inbound_calls: "loading...",
      total_calls: "loading...",
      answered_calls: "loading...",
      unanswered_calls: "loading...",
      missed_calls: "loading...",
    });
    try {
      const response = await getDashboardAcquisitionTilesCount(
        customDateRange.fromDate,
        customDateRange.toDate
      );
      if (response.success === true && response.message === "Success") {
        const acquisitionData =
          response.data.LeadTilesCount.acquisitionCount[0] || {};
        const phoneCallData =
          response.data.LeadTilesCount.phoneCallCount.reduce(
            (acc, curr) => {
              const key = curr.phonecall_out.toLowerCase().includes("inbound")
                ? "inbound_calls"
                : "outbound_calls";
              acc[key] = parseInt(curr.count_out, 10); // Parse count_out to integer
              return acc;
            },
            { inbound_calls: 0, outbound_calls: 0 }
          ); // Initialize with defaults

        // Calculate total calls as sum of inbound and outbound
        const totalCalls =
          phoneCallData.inbound_calls + phoneCallData.outbound_calls;

        const statusData = response.data.LeadTilesCount.statusCount.reduce(
          (acc, curr) => {
            let key = curr.status_out.toLowerCase() + "_calls";
            if (curr.status_out === "NotAnswered") key = "unanswered_calls";
            acc[key] = parseInt(curr.count_out, 10); // Parse count_out to integer
            return acc;
          },
          { answered_calls: 0, missed_calls: 0, unanswered_calls: 0 }
        ); // Initialize with defaults

        setMetrics({
          engagement_count: parseInt(
            acquisitionData.engagement_percentage || 0,
            10
          ),
          whatsapp_count: parseInt(acquisitionData.whatsapp_count || 0, 10),
          diy_lead_count: parseInt(acquisitionData.diy_lead_count || 0, 10),
          dropoff_count: parseInt(acquisitionData.dropoff_count || 0, 10),
          new_leads_count: parseInt(acquisitionData.new_leads_count || 0, 10),
          ...phoneCallData,
          total_calls: totalCalls,
          ...statusData,
        });
      } else {
        showToast("Acquisition widgets Update Unsuccessful!!!", "error");
      }
    } catch (error) {
      console.error("Something went wrong !!");
    }
  };

  const leadSources = async () => {
    setIsLoading(true);
    try {
      const response = await getGraphReportLeadSource(
        customDateRange.fromDate,
        customDateRange.toDate
      );
      if (response.success === true && response.message === "Success") {
        const reportLeadSources = response.data.ReportLeadSources;
        const labels = reportLeadSources.map((item) => item.source_medium);
        const data = reportLeadSources.map((item) => parseInt(item.count, 10));

        // Update graphData1 state with new labels and data
        setGraphData1({
          ...graphData1,
          labels: labels,
          datasets: [
            {
              ...graphData1.datasets[0],
              data: data,
            },
          ],
        });
      } else {
        showToast("Lead sources graph update unsuccessful!!!", "error");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const leadPaymentInsight = async () => {
    try {
      const response = await getGraphReportLeadPaymentInsight(
        customDateRange.fromDate,
        customDateRange.toDate
      );
      if (response.success === true && response.message === "Success") {
        const responseData = response.data.ReportLeadPayment;
        // Create arrays for labels and data
        const labels = [];
        const data = [];
        // Populate the labels and data arrays
        responseData.forEach((item) => {
          const [key, value] = Object.entries(item)[0]; // Assuming each item has only one key-value pair
          labels.push(key);
          data.push(parseInt(value, 10)); // Convert string to integer
        });
        // Update graphData3 with the new labels and data
        setGraphData3((prevGraphData) => ({
          ...prevGraphData,
          labels: labels,
          datasets: [
            {
              ...prevGraphData.datasets[0],
              data: data,
            },
          ],
        }));
      } else {
        showToast("Lead Payment Insight graph Update Unsuccessful!!!", "error");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const leadConversionGraphPerDay = async () => {
    try {
      const response = await getGraphReportLeadConversionPerDay();
      if (response.success === true && response.message === "Success") {
        const { totalLeads, paymentLeads } = response.data.LeadConversionCount;

        // Process the data for the chart
        const labels = totalLeads.map((item) => formatDate(item.created_date));
        const totalLeadsData = totalLeads.map((item) =>
          parseInt(item.lead_count, 10)
        );
        const paymentLeadsData = paymentLeads.map((item) =>
          parseInt(item.lead_count, 10)
        );

        // Update the multiAxisChartData state
        setMultiAxisLineChartData({
          labels,
          datasets: [
            { ...multiAxisLineChartData.datasets[0], data: totalLeadsData },
            { ...multiAxisLineChartData.datasets[1], data: paymentLeadsData },
          ],
        });
      } else {
        showToast("Lead Payment graph Update Unsuccessful!!!", "error");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const redisLeadMetrics = async (fromDate, toDate, timeline) => {
    console.log(fromDate, toDate, timeline);

    setMetrics({
      engagement_count: "loading...",
      whatsapp_count: "loading...",
      diy_lead_count: "loading...",
      dropoff_count: "loading...",
      new_leads_count: "loading...",
      outbound_calls: "loading...",
      inbound_calls: "loading...",
      total_calls: "loading...",
      answered_calls: "loading...",
      unanswered_calls: "loading...",
      missed_calls: "loading...",
    });
    try {
      const response = await getRedisDashboardAcquisitionTilesCount(
        fromDate,
        toDate,
        timeline
      );
      if (response.success === true && response.message === "Success") {
        const acquisitionData =
          response.data.LeadTilesCount.acquisitionCount[0] || {};
        const phoneCallData =
          response.data.LeadTilesCount.phoneCallCount.reduce(
            (acc, curr) => {
              const key = curr.phonecall_out.toLowerCase().includes("inbound")
                ? "inbound_calls"
                : "outbound_calls";
              acc[key] = parseInt(curr.count_out, 10); // Parse count_out to integer
              return acc;
            },
            { inbound_calls: 0, outbound_calls: 0 }
          ); // Initialize with defaults

        // Calculate total calls as sum of inbound and outbound
        const totalCalls =
          phoneCallData.inbound_calls + phoneCallData.outbound_calls;

        const statusData = response.data.LeadTilesCount.statusCount.reduce(
          (acc, curr) => {
            let key = curr.status_out.toLowerCase() + "_calls";
            if (curr.status_out === "NotAnswered") key = "unanswered_calls";
            acc[key] = parseInt(curr.count_out, 10); // Parse count_out to integer
            return acc;
          },
          { answered_calls: 0, missed_calls: 0, unanswered_calls: 0 }
        );

        setMetrics({
          engagement_count: parseInt(
            acquisitionData.engagement_percentage || 0,
            10
          ),
          whatsapp_count: parseInt(acquisitionData.whatsapp_count || 0, 10),
          diy_lead_count: parseInt(acquisitionData.diy_lead_count || 0, 10),
          dropoff_count: parseInt(acquisitionData.dropoff_count || 0, 10),
          new_leads_count: parseInt(acquisitionData.new_leads_count || 0, 10),
          ...phoneCallData,
          total_calls: totalCalls,
          ...statusData,
        });
      } else {
        showToast("Acquisition widgets Update Unsuccessful!!!", "error");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const redisLeadSources = async (fromDate, toDate, timeline) => {
    console.log(fromDate, toDate, timeline);
    try {
      const response = await getRedisGraphReportLeadSource(
        fromDate,
        toDate,
        timeline
      );
      if (response.success === true && response.message === "Success") {
        const reportLeadSources = response.data.ReportLeadSources;
        const labels = reportLeadSources.map((item) => item.source_medium);
        const data = reportLeadSources.map((item) => parseInt(item.count, 10));

        // Update graphData1 state with new labels and data
        setGraphData1({
          ...graphData1,
          labels: labels,
          datasets: [
            {
              ...graphData1.datasets[0],
              data: data,
            },
          ],
        });
      } else {
        showToast("Lead sources graph update unsuccessful!!!", "error");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const redisLeadPaymentInsight = async (fromDate, toDate, timeline) => {
    console.log(fromDate, toDate, timeline);
    try {
      const response = await getRedisGraphReportLeadPaymentInsight(
        fromDate,
        toDate,
        timeline
      );
      if (response.success === true && response.message === "Success") {
        const responseData = response.data.ReportLeadPayment;
        // Create arrays for labels and data
        const labels = [];
        const data = [];
        // Populate the labels and data arrays
        responseData.forEach((item) => {
          const [key, value] = Object.entries(item)[0]; // Assuming each item has only one key-value pair
          labels.push(key);
          data.push(parseInt(value, 10)); // Convert string to integer
        });
        // Update graphData3 with the new labels and data
        setGraphData3((prevGraphData) => ({
          ...prevGraphData,
          labels: labels,
          datasets: [
            {
              ...prevGraphData.datasets[0],
              data: data,
            },
          ],
        }));
      } else {
        showToast("Lead Payment Insight graph Update Unsuccessful!!!", "error");
      }
    } catch (error) {
      console.error(error);
    }
  };
  const redisLeadConversionGraphPerDay = async () => {
    try {
      const response = await getRedisGraphReportLeadConversionPerDay();
      if (response.success === true && response.message === "Success") {
        const { totalLeads, paymentLeads } = response.data.LeadConversionCount;

        // Process the data for the chart
        const labels = totalLeads.map((item) => formatDate(item.created_date));
        const totalLeadsData = totalLeads.map((item) =>
          parseInt(item.lead_count, 10)
        );
        const paymentLeadsData = paymentLeads.map((item) =>
          parseInt(item.lead_count, 10)
        );

        // Update the multiAxisChartData state
        setMultiAxisLineChartData({
          labels,
          datasets: [
            { ...multiAxisLineChartData.datasets[0], data: totalLeadsData },
            { ...multiAxisLineChartData.datasets[1], data: paymentLeadsData },
          ],
        });
      } else {
        showToast("Lead Payment graph Update Unsuccessful!!!", "error");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getDataByRedis = (timeline) => {
    let fromDate;
    let toDate;
    const todayDate = dayjs().format("YYYY-MM-DD");
    const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
    const lastWeekStart = dayjs()
      .subtract(1, "week")
      .startOf("isoWeek")
      .format("YYYY-MM-DD");
    const lastWeekEnd = dayjs()
      .subtract(1, "week")
      .endOf("isoWeek")
      .format("YYYY-MM-DD");
    const thisWeekStart = dayjs().startOf("isoWeek").format("YYYY-MM-DD");
    const lastMonthStart = dayjs()
      .subtract(1, "month")
      .startOf("month")
      .format("YYYY-MM-DD");
    const lastMonthEnd = dayjs()
      .subtract(1, "month")
      .endOf("month")
      .format("YYYY-MM-DD");
    const thisMonthStart = dayjs().startOf("month").format("YYYY-MM-DD");
    const sevenDaysAgo = dayjs().subtract(6, "day").format("YYYY-MM-DD");
    const thirtyDaysAgo = dayjs().subtract(30, "day").format("YYYY-MM-DD");

    if (timeline === "today") {
      fromDate = todayDate;
      toDate = todayDate;
    } else if (timeline === "yesterday") {
      fromDate = yesterday;
      toDate = yesterday;
    } else if (timeline === "alltime") {
      fromDate = "2019-01-01";
      toDate = todayDate;
    } else if (timeline === "lastweek") {
      fromDate = lastWeekStart;
      toDate = lastWeekEnd;
    } else if (timeline === "thisweek") {
      fromDate = thisWeekStart;
      toDate = todayDate;
    } else if (timeline === "lastmonth") {
      fromDate = lastMonthStart;
      toDate = lastMonthEnd;
    } else if (timeline === "thismonth") {
      fromDate = thisMonthStart;
      toDate = todayDate;
    } else if (timeline === "last7days") {
      fromDate = sevenDaysAgo;
      toDate = todayDate;
    } else if (timeline === "last30days") {
      fromDate = thirtyDaysAgo;
      toDate = todayDate;
    }
    redisLeadMetrics(fromDate, toDate, timeline);
    redisLeadSources(fromDate, toDate, timeline);
    redisLeadPaymentInsight(fromDate, toDate, timeline);
    redisLeadConversionGraphPerDay();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const nth = (d) => {
      if (d > 3 && d < 21) return "th";
      switch (d % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };
    return `${date.getDate()}${nth(date.getDate())} ${date.toLocaleString(
      "default",
      { month: "short" }
    )}`;
  };

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
        display: true,
        labels: {
          borderWidth: 0,
        },
      },
    },
  };

  const pieChartOptions = {
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
      },
    },
    responsive: true,
  };

  const toggleFullScreen = (graphId) => {
    if (fullScreenGraph === graphId) {
      setFullScreenGraph(null);
    } else {
      setFullScreenGraph(graphId);
    }
  };

  const leadMetricsData = [
    {
      title: "New Leads",
      dataValueName: "new_leads_count",
      today: metrics.new_leads_count,
    },
    {
      title: "Engagement",
      dataValueName: "engagement_count",
      today: `${
        metrics.engagement_count !== "loading..."
          ? metrics.engagement_count + "%"
          : "loading..."
      }`,
      overall: "0%",
    },
    {
      title: "Drop Off Leads",
      dataValueName: "dropoff_count",
      today: metrics.dropoff_count,
    },
    {
      title: "DIY Leads",
      dataValueName: "diy_lead_count",
      today: metrics.diy_lead_count,
    },
    {
      title: "Whatsapp Engage",
      dataValueName: "whatsapp_count",
      today: metrics.whatsapp_count,
    },
    {
      title: "Total Calls",
      dataValueName: "total_calls",
      today: metrics.total_calls,
    },
    {
      title: "Answered Calls",
      dataValueName: "answered_calls",
      today: metrics.answered_calls,
    },
    {
      title: "Unanswered Calls",
      dataValueName: "unanswered_calls",
      today: metrics.unanswered_calls,
    },
    {
      title: "Missed Calls",
      dataValueName: "missed_calls",
      today: metrics.missed_calls,
    },
  ];

  const handleTimelineChange = (value) => {
    setShowDatePicker(false);
    switch (value) {
      case "All Time":
        setDateRangeType("alltime");
        getDataByRedis("alltime");
        break;
      case "Custom Date":
        setDateRangeType("custom");
        setShowDatePicker(true);
        break;
      case "Yesterday": {
        setDateRangeType("yesterday");
        getDataByRedis("yesterday");
        break;
      }
      case "Today": {
        setDateRangeType("today");
        getDataByRedis("today");
        break;
      }
      case "Last Week": {
        setDateRangeType("lastweek");
        getDataByRedis("lastweek");
        break;
      }

      case "This Week": {
        setDateRangeType("thisweek");
        getDataByRedis("thisweek");
        break;
      }
      case "Last Month": {
        setDateRangeType("lastmonth");
        getDataByRedis("lastmonth");
        break;
      }
      case "This Month": {
        setDateRangeType("thismonth");
        getDataByRedis("thismonth");
        break;
      }
      case "Last 7 Days": {
        setDateRangeType("last7days");
        getDataByRedis("last7days");
        break;
      }
      case "Last 30 Days": {
        setDateRangeType("last30days");
        getDataByRedis("last30days");
        break;
      }
      default:
        console.log("Unexpected value:", value);
        break;
    }
  };

  const handleCustomFromDateChange = (value) => {
    setCustomDateRange((prev) => ({ ...prev, fromDate: value }));
  };

  const handleCustomToDateChange = (value) => {
    setCustomDateRange((prev) => ({ ...prev, toDate: value }));
  };

  const handleGoButton = () => {
    if (customDateRange.fromDate && customDateRange.toDate) {
      try {
        leadSources();
        leadPaymentInsight();
        leadMetrics();
        leadConversionGraphPerDay();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleDateClearButton = () => {
    setCustomDateRange({ fromDate: "", toDate: "" });
    getDataByRedis("today");
  };

  return (
    <div style={styles.mainContent}>
      <div style={styles.dashboardBar}>
        <div style={styles.dashboardText}>
          <p style={{ margin: 0 }}>Leads Dashboard</p>
        </div>
        <div style={styles.headerIcon}>
          <DashboardTimelineSelector
            onChange={(value) => handleTimelineChange(value)}
          />
          {showDatePicker && (
            <MUIPairDatePicker
              showGoButton="true"
              onFromDateChange={handleCustomFromDateChange}
              onToDateChange={handleCustomToDateChange}
              fromDate={customDateRange.fromDate}
              toDate={customDateRange.toDate}
              onClick={handleGoButton}
              onClose={handleDateClearButton}
            />
          )}
        </div>
      </div>
      <div style={styles.scrollableContent}>
        <div style={styles.titleSection}>
          <p
            style={{
              fontSize: "20px",
            }}
          >
            Acquisition
          </p>
          <RefreshButton handleRefresh={handleRefresh} />
        </div>
        <div style={styles.cardsRow}>
          {leadMetricsData.map((item, index) => (
            <DashboardCard
              key={index}
              title={item.title}
              data={metrics[item.dataValueName]}
            />
          ))}
        </div>
        <div style={styles.contentContainer}>
          <GraphComponent
            graphId="graph1"
            graphData={graphData1}
            title="Lead By Sources"
            graphType={graph1Type}
            pieChartOptions={pieChartOptions}
            isFullScreen={fullScreenGraph === "graph1"}
            toggleFullScreen={() => toggleFullScreen("graph1")}
            iconsToShow={["bar", "pie", "arrow"]}
            onChangeGraphType={setGraph1Type}
            graphOptions={{
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: "Number Of Leads",
                  },
                },
                x: {
                  title: {
                    display: true,
                    text: "Source Medium",
                  },
                },
              },
              plugins: {
                legend: {
                  display: false,
                },
              },
            }}
            isLoading={isLoading}
            hasData={graphData1.datasets[0].data.length > 0}
          />

          <GraphComponent
            graphId="graph2"
            graphData={multiAxisLineChartData}
            title="Leads & Conversions Per Day"
            graphType={graph2Type}
            pieChartOptions={pieChartOptions}
            isFullScreen={fullScreenGraph === "graph2"}
            toggleFullScreen={() => toggleFullScreen("graph2")}
            iconsToShow={["arrow"]}
            onChangeGraphType={setGraph2Type}
            graphOptions={lineChartOptions}
            isLoading={isLoading}
            hasData={multiAxisLineChartData.datasets[0].data.length > 0}
          />
          <GraphComponent
            graphId="graph3"
            graphData={graphData3}
            title="Lead Payment Insight"
            graphType={graph3Type}
            pieChartOptions={pieChartOptions}
            isFullScreen={fullScreenGraph === "graph3"}
            toggleFullScreen={() => toggleFullScreen("graph3")}
            iconsToShow={["line", "pie", "arrow"]}
            onChangeGraphType={setGraph3Type}
            graphOptions={{
              responsive: true,
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: "Purchase Rate",
                  },
                },
                x: {
                  title: {
                    display: true,
                    text: "Purchase Plan",
                  },
                },
              },
              plugins: {
                legend: {
                  display: false,
                },
              },
            }}
            isLoading={isLoading}
            hasData={graphData3.datasets[0].data.length > 0}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

const styles = {
  dashboardContainer: {
    display: "flex",
    height: "100vh",
    backgroundColor: "#F1F4FA",
  },
  mainContent: {
    height: "100vh",
    flex: 1,
    backgroundColor: "#F1F4FA",
  },
  dashboardBar: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0px",
  },
  dashboardText: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "20px",
    margin: 0,
    color: "#06152B",
    fontSize: "25px",
    fontWeight: "600",
  },
  headerIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "20px",
    marginRight: "20px",
    flex: 1,
    paddingTop: "20px",
  },
  scrollableContent: {
    height: "calc(100vh - 100px)",
    overflow: "scroll",
  },
  titleSection: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: "20px",
  },
  cardsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "15px",
  },
  contentContainer: {
    display: "flex",
    marginTop: "20px",
    gap: "15px",
    flexWrap: "wrap",
  },
};
